"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, User, Users, Workflow as WorkflowIcon } from "lucide-react";
import { useWorkflows } from "@stockline/web";
import { ContextEditor } from "@/components/context-editor";
import { Label } from "@stockline/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@stockline/ui/components/select";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  useUpsertWorkflowInstruction,
  useWorkflowInstructions,
} from "@/query/hooks/useWorkflowInstructions";
import type {
  Scope,
  WorkflowInstruction,
} from "@/lib/v2-api/workflow-instructions";

interface WorkflowInstructionsBlockProps {
  /** Current user id — target for user-scope (scope=2) rows. */
  userId: string;
  /** Whether the caller may edit company-scope (scope=0) instructions. */
  canEditCompany: boolean;
  /** Whether the group scope is available (the caller belongs to ≥1 group). */
  hasGroups: boolean;
  /** The group currently selected in the Group Context section (shared). */
  selectedGroupId: string | null;
  /** Display name of the selected group, for the group card heading. */
  selectedGroupName: string | null;
  /** Whether the caller may edit the selected group's instructions. */
  canEditSelectedGroup: boolean;
  layout?: "stacked" | "grid";
}

/**
 * Per-workflow AI instructions, presented as a native tier of the Context tab
 * below the general company/user/group context cards. A single workflow
 * dropdown drives up to three scope cards (company / user / group) that mirror
 * the general-context cards one-for-one and reuse the same {@link ContextEditor}
 * and permission gating. Replaces the earlier `GeneralContextDialog` bolt-on
 * (STK-1432): the backend (V2 API + AI prompt injection) is unchanged; only the
 * presentation moved onto the page.
 */
export function WorkflowInstructionsBlock({
  userId,
  canEditCompany,
  hasGroups,
  selectedGroupId,
  selectedGroupName,
  canEditSelectedGroup,
  layout = "stacked",
}: Readonly<WorkflowInstructionsBlockProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();

  // Filter active workflows in memory: the reactive Dexie engine can't index a
  // boolean `where` (IndexedDB rejects boolean keys), so `useWorkflows({
  // isActive: true })` reads empty. Fetch all + filter here.
  const workflows = useWorkflows().filter((w) => w.isActive);

  const [workflowId, setWorkflowId] = useState<string | undefined>(undefined);
  // Keep the picked workflow valid as the list loads/changes; default to the
  // first active workflow so a scope card is always visible (mirrors the
  // group-picker auto-select in useGroupContextEditor).
  useEffect(() => {
    if (workflows.length === 0) {
      setWorkflowId(undefined);
      return;
    }
    setWorkflowId((cur) =>
      cur && workflows.some((w) => w.id === cur) ? cur : workflows[0].id,
    );
  }, [workflows]);

  const { data: rows } = useWorkflowInstructions(workflowId);

  // Group card heading: base title, suffixed with the selected group's name
  // when one is chosen. Precomputed to avoid nested template literals inline.
  const groupTitle = t("common.aiManagement.workflowInstructions.groupTitle");
  const groupCardLabel = selectedGroupName
    ? `${groupTitle} — ${selectedGroupName}`
    : groupTitle;

  if (workflows.length === 0) return null;

  return (
    <div className="border-t border-border/60 pt-4">
      <div className="flex items-center gap-2.5 px-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-500/10">
          <WorkflowIcon className="h-5 w-5 text-slate-500" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {t("common.aiManagement.workflowInstructions.title")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("common.aiManagement.workflowInstructions.description")}
          </p>
        </div>
      </div>

      <div className="mt-3 px-2">
        <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
          {t("common.aiManagement.workflowInstructions.workflowPickerLabel")}
        </Label>
        <Select value={workflowId} onValueChange={setWorkflowId}>
          <SelectTrigger
            className="mt-1 h-8 text-sm"
            data-testid="workflow-instructions-picker"
          >
            <SelectValue
              placeholder={t(
                "common.aiManagement.workflowInstructions.selectWorkflow",
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {workflows.map((w) => (
              <SelectItem
                key={w.id}
                value={w.id}
                data-testid={`workflow-instructions-option-${w.id}`}
              >
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {workflowId && (
        <div
          className={cn(
            "mt-3",
            layout === "grid"
              ? "grid grid-cols-1 lg:grid-cols-2 gap-3"
              : "space-y-3",
          )}
        >
          <ScopeCard
            testid="workflow-instructions-company"
            icon={<Building2 className="h-5 w-5 text-blue-500" />}
            iconBg="bg-blue-500/10"
            label={t("common.aiManagement.workflowInstructions.companyTitle")}
            workflowId={workflowId}
            scope={0}
            targetGroupId={null}
            targetUserId={null}
            rows={rows}
            canEdit={canEditCompany}
            title={t("common.aiManagement.workflowInstructions.companyTitle")}
            description={
              canEditCompany
                ? t(
                    "common.aiManagement.workflowInstructions.companyDescription",
                  )
                : t("common.aiManagement.noPermission")
            }
            placeholder={t(
              "common.aiManagement.workflowInstructions.companyPlaceholder",
            )}
            layout={layout}
            onToast={toast}
          />

          <ScopeCard
            testid="workflow-instructions-user"
            icon={<User className="h-5 w-5 text-green-500" />}
            iconBg="bg-green-500/10"
            label={t("common.aiManagement.workflowInstructions.userTitle")}
            workflowId={workflowId}
            scope={2}
            targetGroupId={null}
            targetUserId={userId}
            rows={rows}
            canEdit
            title={t("common.aiManagement.workflowInstructions.userTitle")}
            description={t(
              "common.aiManagement.workflowInstructions.userDescription",
            )}
            placeholder={t(
              "common.aiManagement.workflowInstructions.userPlaceholder",
            )}
            layout={layout}
            onToast={toast}
          />

          {hasGroups && selectedGroupId && (
            <ScopeCard
              testid="workflow-instructions-group"
              icon={<Users className="h-5 w-5 text-violet-500" />}
              iconBg="bg-violet-500/10"
              label={groupCardLabel}
              workflowId={workflowId}
              scope={1}
              targetGroupId={selectedGroupId}
              targetUserId={null}
              rows={rows}
              canEdit={canEditSelectedGroup}
              title={groupCardLabel}
              description={
                canEditSelectedGroup
                  ? t(
                      "common.aiManagement.workflowInstructions.groupDescription",
                    )
                  : t("common.aiManagement.noPermission")
              }
              placeholder={t(
                "common.aiManagement.workflowInstructions.groupPlaceholder",
              )}
              layout={layout}
              onToast={toast}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface ScopeCardProps {
  testid: string;
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  workflowId: string;
  scope: Scope;
  targetGroupId: string | null;
  targetUserId: string | null;
  rows: WorkflowInstruction[] | undefined;
  canEdit: boolean;
  title: string;
  description: string;
  placeholder: string;
  layout: "stacked" | "grid";
  onToast: ReturnType<typeof useToast>["toast"];
}

/**
 * One workflow-scope card: an icon+label header (mirroring the general-context
 * cards) over a {@link ContextEditor} bound to the (workflow, scope, target)
 * instruction row. Empty content deletes the row (the V2 API upsert does this).
 */
function ScopeCard({
  testid,
  icon,
  iconBg,
  label,
  workflowId,
  scope,
  targetGroupId,
  targetUserId,
  rows,
  canEdit,
  title,
  description,
  placeholder,
  layout,
  onToast,
}: Readonly<ScopeCardProps>) {
  const { t } = useTranslation();
  const upsert = useUpsertWorkflowInstruction();

  const existing = useMemo(
    () =>
      (rows ?? []).find(
        (r) =>
          r.scope === scope &&
          (scope !== 1 || r.targetGroupId === targetGroupId) &&
          (scope !== 2 || r.targetUserId === targetUserId),
      ),
    [rows, scope, targetGroupId, targetUserId],
  );
  const stored = existing?.content ?? "";

  // Draft is null until the user edits, so the card always reflects the latest
  // fetched value; reset it whenever the identity of the edited row changes.
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => {
    setDraft(null);
  }, [workflowId, targetGroupId, targetUserId]);

  const value = draft ?? stored;
  const isDirty = draft !== null && draft !== stored;

  async function onSave() {
    try {
      await upsert.mutateAsync({
        workflowId,
        scope,
        targetGroupId,
        targetUserId,
        content: value,
      });
      setDraft(null);
      onToast({ title: t("common.success") });
    } catch (error) {
      console.error("Failed to save workflow instruction:", error);
      onToast({ title: t("common.error"), variant: "destructive" });
    }
  }

  return (
    <div className="border-y border-border/40 py-2" data-testid={testid}>
      <div className="flex items-center gap-2.5 p-2">
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
            iconBg,
          )}
        >
          {icon}
        </span>
        <span className="text-sm font-medium truncate">{label}</span>
        {isDirty && (
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
            title={t("ui.unsavedChanges")}
            aria-label={t("ui.unsavedChanges")}
          />
        )}
      </div>
      <div className="pt-2">
        <ContextEditor
          value={value}
          onChange={setDraft}
          onSave={onSave}
          onDiscard={() => setDraft(null)}
          isDirty={isDirty}
          isSaving={upsert.isPending}
          isReadOnly={!canEdit}
          title={title}
          description={description}
          placeholder={placeholder}
          layout={layout}
        />
      </div>
    </div>
  );
}
